import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Mention } from "@/schema/cross_reference"
import { BaseInlineInput } from "@ui/forms/inline/base"
import { InlineInputProps } from "@ui/forms/inline/props"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { Plus, X } from "lucide-react"

import { useGlobalSearch } from "@/hooks/use-global-search"
import { useHouseholdNoteMentionables } from "@/hooks/use-household-note-mentionables"
import { MentionDropdown } from "@/components/lexical/plugins/mention-dropdown/mention-dropdown"
import { MentionOption } from "@/components/lexical/plugins/mention-dropdown/types"
import PeekView from "@/components/peek-view"

interface MentionsHeaderProps extends Pick<InlineInputProps, "fixedLabelWidth" | "LabelIcon"> {
  mentions: Mention[]
  onMentionsChange?: (mentions: Mention[]) => void
  householdId?: string | null
  bookId?: string
  teamOptions?: MentionOption[]
  disabled?: boolean
}

const ALLOWED_MENTION_TYPES = [
  "client",
  "prospect",
  "person",
  "business",
  "contact",
  "household",
  "user",
  "trust",
] as const

export function MentionsHeader({
  mentions,
  onMentionsChange,
  disabled,
  fixedLabelWidth,
  LabelIcon,
  householdId,
  bookId,
  teamOptions,
}: MentionsHeaderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { results, handleSearchChange, isSearching, searchQuery } = useGlobalSearch({
    allowedTypes: ALLOWED_MENTION_TYPES,
    bookScopedUsers: true,
    bookIdOverride: bookId,
  })
  const { filterMentionables } = useHouseholdNoteMentionables(householdId)

  const mentionOptions: MentionOption[] = useMemo(() => {
    const householdResults = filterMentionables(searchQuery)
    const seen = new Set(householdResults.map((result) => `${result.type}-${result.id}`))
    const merged = [...householdResults, ...results.filter((result) => !seen.has(`${result.type}-${result.id}`))]
    const matchingTeams =
      teamOptions?.filter((option) => option.label.toLowerCase().includes(searchQuery.trim().toLowerCase())) ?? []
    return [...matchingTeams, ...merged.map((result) => new MentionOption(result))]
  }, [filterMentionables, results, searchQuery, teamOptions])

  useEffect(() => {
    setSelectedIndex(0)
  }, [mentionOptions])

  const handleSelect = useCallback(
    (option: MentionOption) => {
      if (!onMentionsChange) return

      const newMention: Mention = {
        target_id: option.recordId,
        target_type: option.recordType as Mention["target_type"],
        target_label: option.label,
      }

      const exists = mentions.some(
        (m) => m.target_id === newMention.target_id && m.target_type === newMention.target_type
      )
      if (!exists) {
        onMentionsChange([...mentions, newMention])
      }

      setIsOpen(false)
      handleSearchChange("")
    },
    [mentions, onMentionsChange, handleSearchChange]
  )

  const handleRemove = useCallback(
    (mention: Mention) => {
      if (!onMentionsChange) return
      onMentionsChange(
        mentions.filter((m) => !(m.target_id === mention.target_id && m.target_type === mention.target_type))
      )
    },
    [mentions, onMentionsChange]
  )

  const SUPPORTED_PEEK_TYPES: Mention["target_type"][] = ["Client", "Prospect", "Household", "User", "Person", "Trust"]

  const getPeekViewType = (
    type: Mention["target_type"]
  ): "Client" | "Prospect" | "Household" | "User" | "Person" | "Trust" | null => {
    if (SUPPORTED_PEEK_TYPES.includes(type)) {
      return type as "Client" | "Prospect" | "Household" | "User" | "Person" | "Trust"
    }
    return null
  }

  return (
    <BaseInlineInput label="References" LabelIcon={LabelIcon} fixedLabelWidth={fixedLabelWidth}>
      <div className="flex min-h-6 w-full flex-wrap items-center gap-x-3 gap-y-1 px-2">
        {mentions.map((mention) => {
          const peekType = getPeekViewType(mention.target_type)
          return (
            <div key={`${mention.target_type}-${mention.target_id}`} className="group inline-flex items-center gap-1">
              {peekType ? (
                <PeekView recordType={peekType} recordId={mention.target_id} label={mention.target_label} size="sm" />
              ) : (
                <span className="text-sm">{`@ ${mention.target_label}`}</span>
              )}
              <button
                onClick={() => handleRemove(mention)}
                className="hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground rounded-sm p-0.5 transition-colors"
                type="button"
                aria-label={`Remove ${mention.target_label}`}
                disabled={disabled}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )
        })}

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-primary -ml-2 flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm focus:outline-1"
              disabled={disabled}
            >
              <Plus className="size-3.5" />
              Add reference
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-0"
            align="start"
            onOpenAutoFocus={(e) => {
              e.preventDefault()
              searchInputRef.current?.focus()
            }}
          >
            <MentionDropdown
              options={mentionOptions}
              selectedIndex={selectedIndex}
              searching={isSearching}
              searchQuery={searchQuery}
              onSelect={handleSelect}
              onSearchChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault()
                  setSelectedIndex((prev) => Math.min(prev + 1, mentionOptions.length - 1))
                } else if (e.key === "ArrowUp") {
                  e.preventDefault()
                  setSelectedIndex((prev) => Math.max(prev - 1, 0))
                } else if (e.key === "Enter" && mentionOptions.length > 0) {
                  e.preventDefault()
                  handleSelect(mentionOptions[selectedIndex])
                } else if (e.key === "Escape") {
                  setIsOpen(false)
                }
              }}
              searchInputRef={searchInputRef}
            />
          </PopoverContent>
        </Popover>
      </div>
    </BaseInlineInput>
  )
}
